import { useMemo, useState } from "react";
import { Plane, Sun } from "lucide-react";
import { useForumColors } from "@/context/ForumColorContext";
import { alpha, readableOn } from "@/lib/forumColors";
import { fmtTime, isAllDay, minutesOf, whenLabel } from "@/lib/eventMeta";

const DEFAULT_OPEN = 9 * 60;
const DEFAULT_CLOSE = 20 * 60;
const FIXED_BLOCK = 45;
const LUNCH_MIN_GAP = 5;

const hhmm = (minutes) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

// no am/pm — the axis above already gives that, and the band is narrow
const compact = (minutes) => `${Math.floor(minutes / 60) % 12 || 12}:${String(minutes % 60).padStart(2, "0")}`;

const segmentsFor = (event, court) => {
  const start = minutesOf(event.start_time);
  if (start !== null) {
    const end = minutesOf(event.end_time);
    return [{ start, end: end !== null && end > start ? end : start + FIXED_BLOCK }];
  }
  if (event.event_type !== "session_based") return null;
  const s1s = minutesOf(court?.session_1_start) ?? 630;
  const s1e = minutesOf(court?.session_1_end) ?? 810;
  const s2s = minutesOf(court?.session_2_start) ?? 870;
  const s2e = minutesOf(court?.session_2_end) ?? 990;
  if (event.session_flag === "session_1") return [{ start: s1s, end: s1e }];
  if (event.session_flag === "session_2") return [{ start: s2s, end: s2e }];
  // both sessions: draw two bars so the lunch adjournment reads as a gap, not sitting time
  if (s2s - s1e >= LUNCH_MIN_GAP) {
    return [
      { start: s1s, end: s1e, label: "Session 1" },
      { start: s2s, end: s2e, label: "Session 2" },
    ];
  }
  return [{ start: s1s, end: s2e }];
};

export const DayTimeline = ({ events, onOpenEvent }) => {
  const { colorFor, forumName, courts } = useForumColors();
  const [focus, setFocus] = useState(null);
  const courtsById = useMemo(
    () => Object.fromEntries(courts.map((c) => [c.court_id, c])),
    [courts],
  );

  const fullDay = events.filter((e) => isAllDay(e));
  const blocks = useMemo(() => {
    const entries = [];
    events
      .filter((e) => !isAllDay(e))
      .forEach((event) => {
        const segs = segmentsFor(event, courtsById[event.court_id]);
        if (segs?.length) entries.push({ event, segs });
      });
    entries.sort(
      (a, b) => a.segs[0].start - b.segs[0].start || a.segs[0].end - b.segs[0].end,
    );
    const laneEnds = [];
    const list = [];
    entries.forEach(({ event, segs }) => {
      // one lane per hearing, so both halves of a full-day matter share a row
      const from = segs[0].start;
      const to = segs[segs.length - 1].end;
      let lane = laneEnds.findIndex((end) => end <= from);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(to);
      } else {
        laneEnds[lane] = to;
      }
      segs.forEach((seg, part) => list.push({ event, ...seg, lane, part }));
    });
    return { list, lanes: Math.max(1, laneEnds.length) };
  }, [events, courtsById]);

  const [open, close] = useMemo(() => {
    const opens = courts.map((c) => minutesOf(c.opens_at)).filter((m) => m !== null);
    const closes = courts.map((c) => minutesOf(c.closes_at)).filter((m) => m !== null);
    let from = Math.min(DEFAULT_OPEN, ...(opens.length ? opens : [DEFAULT_OPEN]));
    let to = Math.max(DEFAULT_CLOSE, ...(closes.length ? closes : [DEFAULT_CLOSE]));
    blocks.list.forEach((b) => {
      from = Math.min(from, b.start - 15);
      to = Math.max(to, b.end + 15);
    });
    return [Math.floor(from / 60) * 60, Math.ceil(to / 60) * 60];
  }, [courts, blocks]);

  const total = Math.max(60, close - open);
  const pct = (minutes) => ((minutes - open) / total) * 100;
  const hours = [];
  for (let m = open; m <= close; m += 60) hours.push(m);

  // the court's own break — the window every listed forum is actually down
  const lunchBand = useMemo(() => {
    let from = null;
    let to = null;
    events.forEach((event) => {
      const court = courtsById[event.court_id];
      if (!court) return;
      const s1e = minutesOf(court.session_1_end);
      const s2s = minutesOf(court.session_2_start);
      if (s1e === null || s2s === null || s2s - s1e < LUNCH_MIN_GAP) return;
      from = from === null ? s1e : Math.max(from, s1e);
      to = to === null ? s2s : Math.min(to, s2s);
    });
    return from !== null && to - from >= LUNCH_MIN_GAP ? { from, to } : null;
  }, [events, courtsById]);

  const trackMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const minute = open + ratio * total;
    const hits = blocks.list.filter((b) => minute >= b.start - 6 && minute <= b.end + 6);
    setFocus({ ratio, minute, events: hits.map((b) => b.event) });
  };

  return (
    <div className="space-y-3" data-testid="day-timeline">
      {fullDay.length ? (
        <div className="space-y-1.5" data-testid="day-timeline-fullday">
          {fullDay.map((e) => {
            const color = colorFor(e);
            const travel = e.event_type === "out_of_station";
            return (
              <button
                key={e.event_id}
                data-testid={`timeline-fullday-${e.event_id}`}
                onClick={() => onOpenEvent?.(e)}
                className="w-full text-left rounded-sm px-3 py-2 border flex items-center gap-2"
                style={{
                  borderColor: alpha(color, 0.6),
                  backgroundImage: `repeating-linear-gradient(135deg, ${alpha(color, 0.32)}, ${alpha(
                    color,
                    0.32,
                  )} 6px, ${alpha(color, 0.08)} 6px, ${alpha(color, 0.08)} 14px)`,
                }}
              >
                {travel ? (
                  <Plane className="h-4 w-4" style={{ color }} />
                ) : (
                  <Sun className="h-4 w-4" style={{ color }} />
                )}
                <span className="text-[0.65rem] font-bold uppercase tracking-[0.12em]" style={{ color }}>
                  {travel ? "Out of station" : "All day"}
                </span>
                <span className="text-sm font-semibold truncate">{e.title}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="relative select-none">
        <div className="flex justify-between mb-1">
          {hours.map((m) => (
            <span key={m} className="text-[0.62rem] font-semibold text-muted-foreground tabular-nums">
              {fmtTime(`${String(Math.floor(m / 60)).padStart(2, "0")}:00`)}
            </span>
          ))}
        </div>

        <div
          data-testid="timeline-track"
          onMouseMove={trackMove}
          onMouseLeave={() => setFocus(null)}
          onClick={trackMove}
          className="relative border border-border rounded-sm bg-secondary/40 cursor-crosshair"
          style={{ height: `${blocks.lanes * 2.4 + 0.8}rem` }}
        >
          {lunchBand
            ? (() => {
                const widthPct = Math.max(0, pct(lunchBand.to) - pct(lunchBand.from));
                return (
                  <span
                    data-testid={`timeline-lunch-${lunchBand.from}`}
                    className="absolute top-0 bottom-0 border-x border-dashed border-foreground/20 pointer-events-none flex items-end justify-center overflow-hidden"
                    style={{
                      left: `${pct(lunchBand.from)}%`,
                      width: `${widthPct}%`,
                      backgroundImage:
                        "repeating-linear-gradient(135deg, rgba(120,113,108,0.24) 0 5px, rgba(120,113,108,0.05) 5px 11px)",
                    }}
                  >
                    <span className="mb-0.5 text-[0.5rem] font-bold uppercase tracking-[0.02em] text-foreground/45 whitespace-nowrap">
                      Lunch
                      {widthPct > 5.5
                        ? ` ${compact(lunchBand.from)}–${compact(lunchBand.to)}`
                        : ""}
                    </span>
                  </span>
                );
              })()
            : null}

          {hours.map((m) => (
            <span
              key={m}
              className="absolute top-0 bottom-0 border-l border-border/70"
              style={{ left: `${pct(m)}%` }}
            />
          ))}

          {blocks.list.map((block) => {
            const color = colorFor(block.event);
            const left = Math.max(0, pct(block.start));
            const width = Math.max(3.5, pct(block.end) - pct(block.start));
            return (
              <button
                key={`${block.event.event_id}-${block.part}`}
                data-testid={
                  block.part === 0
                    ? `timeline-block-${block.event.event_id}`
                    : `timeline-block-${block.event.event_id}-part${block.part + 1}`
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenEvent?.(block.event);
                }}
                onMouseEnter={() =>
                  setFocus({ ratio: (left + width / 2) / 100, minute: block.start, events: [block.event] })
                }
                className="absolute rounded-sm px-2 overflow-hidden text-left hover:brightness-110 transition-all duration-200"
                style={{
                  left: `${left}%`,
                  width: `${Math.min(width, 100 - left)}%`,
                  top: `${block.lane * 2.4 + 0.4}rem`,
                  height: "2rem",
                  background: color,
                  color: readableOn(color),
                  border: block.event.urgent ? "2px solid rgb(var(--c-fixed))" : "none",
                }}
              >
                <span className="block text-[0.68rem] font-bold leading-tight truncate">
                  {block.label ||
                    (block.event.start_time ? fmtTime(block.event.start_time) : whenLabel(block.event))}
                </span>
                <span className="block text-[0.68rem] leading-tight truncate opacity-90">
                  {block.event.title}
                </span>
              </button>
            );
          })}

          {focus ? (
            <span
              className="absolute top-0 bottom-0 w-0.5 bg-foreground/60 pointer-events-none"
              style={{ left: `${focus.ratio * 100}%` }}
            />
          ) : null}
        </div>

        {focus ? (
          <div
            data-testid="timeline-tooltip"
            className="absolute z-20 mt-2 w-64 surface p-3 shadow-lg pointer-events-none"
            style={{
              left: `min(max(0px, calc(${focus.ratio * 100}% - 8rem)), calc(100% - 16rem))`,
            }}
          >
            <p className="label-caps">
              {fmtTime(
                `${String(Math.floor(focus.minute / 60)).padStart(2, "0")}:${String(
                  Math.round(focus.minute % 60),
                ).padStart(2, "0")}`,
              )}
            </p>
            {focus.events.length ? (
              <ul className="mt-2 space-y-2">
                {focus.events.slice(0, 4).map((e) => (
                  <li key={e.event_id} className="flex gap-2">
                    <span
                      className="mt-1 h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ background: colorFor(e) }}
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold leading-snug">{e.title}</span>
                      <span className="block text-xs text-muted-foreground">
                        {[whenLabel(e), forumName(e), e.case_number].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">Nothing listed at this time.</p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};
